import {
  ValidateConfigurationResponse,
  GetDynamicSchemaResponse,
  RefreshPayloadResponse,
} from './src/service-plugins-types.js';
import {
  ValidateConfigurationEnvelope,
  GetDynamicSchemaEnvelope,
  RefreshPayloadEnvelope,
} from './src/interfaces-automations-v1-trigger-provider.public.js';

export * from './src/service-plugins-error-classes';
export * from './src/service-plugins-types';
export {
  ValidateConfigurationEnvelope,
  GetDynamicSchemaEnvelope,
  RefreshPayloadEnvelope,
};

/**
 * This endpoint allows you to run your own logic to validate trigger fields. Implement this endpoint if you need to perform extra validation on top of
 * the Wix validation process.
 *
 * Wix calls Validate Configuration in the following cases:
 *
 * - When a Wix user activates the automation.
 * - When a Wix user selects the automation from the **Your automations** list in the [**Automations**](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Ftriggers/site-automations)
 * dashboard.
 * - When a Wix user opens the automation to edit it.
 *
 * If the user configured filters when setting up their automation, these are included in the request.
 * Wix uses the returned response to display an error message to a user if the requested trigger is invalid.
 * @throws ProviderNotFoundWixError
 */
export declare function validateConfiguration(
  payload: ValidateConfigurationEnvelope
): ValidateConfigurationResponse | Promise<ValidateConfigurationResponse>;
/**
 * This endpoint returns a [JSON schema](https://json-schema.org/) object
 * that's used to dynamically update the fields available to a Wix user
 * when they configure an [automation](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Ftriggers/site-automations).
 *
 * Wix calls this endpoint when a user selects an option from an
 * [item selection filter](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/automations/triggers/filter-fields#item-selection)
 * configured for a trigger.
 * Wix appends the returned schema to the
 * [payload schema](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/automations/triggers/payload-schema)
 * you defined for the trigger and makes the fields available to the Wix user's automation.
 * @throws ProviderNotFoundWixError
 */
export declare function getDynamicSchema(
  payload: GetDynamicSchemaEnvelope
): GetDynamicSchemaResponse | Promise<GetDynamicSchemaResponse>;
/**
 * If an automation's action is delayed, implement this method to retrieve the latest data in the trigger payload before the action fires.
 *
 * For example, if an automation is triggered but its action is set to fire only 24 hours later, Wix calls Refresh Payload before starting
 * the action. This fetches the latest data in the trigger payload. The returned data can be used to update the trigger payload that is passed
 * to the action.
 */
export declare function refreshPayload(
  payload: RefreshPayloadEnvelope
): RefreshPayloadResponse | Promise<RefreshPayloadResponse>;
